import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AnnouncementProvider } from "./contexts/AnnouncementContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Tournaments from "./pages/Tournaments";
import TournamentDetail from "./pages/TournamentDetail";
import Teams from "./pages/Teams";
import Matches from "./pages/Matches";
import LiveScoring from "./pages/LiveScoring";
import PublicScoreboard from "./pages/PublicScoreboard";
import PointsTable from "./pages/PointsTable";
import PlayerStats from "./pages/PlayerStats";
import Announcements from "./pages/Announcements";
import "./App.css";

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
          <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 max-w-lg">
            <h2 className="text-xl font-bold text-red-500 mb-2">Something went wrong</h2>
            <p className="text-gray-300">{this.state.error?.message}</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const OrganizerRoute = ({ children }) => {
  const { user, loading, isOrganizer } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isOrganizer()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AnnouncementProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/live/:matchId" element={<PublicScoreboard />} />
                <Route path="/scoreboard/:matchId" element={<PublicScoreboard />} />
                
                <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/tournaments" element={<ProtectedRoute><Tournaments /></ProtectedRoute>} />
                <Route path="/tournaments/:tournamentId" element={<ProtectedRoute><TournamentDetail /></ProtectedRoute>} />
                <Route path="/tournaments/:tournamentId/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
                <Route path="/tournaments/:tournamentId/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
                <Route path="/tournaments/:tournamentId/points" element={<ProtectedRoute><PointsTable /></ProtectedRoute>} />
                <Route path="/tournaments/:tournamentId/stats" element={<ProtectedRoute><PlayerStats /></ProtectedRoute>} />
                <Route path="/tournaments/:tournamentId/announcements" element={<ProtectedRoute><Announcements /></ProtectedRoute>} />
                <Route path="/scoring/:matchId" element={<OrganizerRoute><LiveScoring /></OrganizerRoute>} />
                
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </AnnouncementProvider>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
